import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Gamepad2, Mail, Lock, ArrowLeft, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const nextPath = searchParams.get('next') || '/dashboard';
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [needsVerify, setNeedsVerify] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Please fill in all fields');
      return;
    }
    setLoading(true);
    try {
      await login(email, password);
      toast.success('Welcome back!');
      navigate(nextPath.startsWith('/') ? nextPath : '/dashboard');
    } catch (error) {
      if (error.response?.data?.detail === 'email_not_verified') {
        setNeedsVerify(true);
        toast.error('Please verify your email first — check your inbox');
      } else {
        toast.error(error.response?.data?.detail || 'Invalid credentials');
      }
    } finally {
      setLoading(false);
    }
  };

  const resendVerification = async () => {
    try {
      await axios.post(`${API}/auth/resend-verification`, { email }, { withCredentials: true });
      toast.success('Verification email sent — check your inbox');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not resend yet'); }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex" data-testid="login-page">
      {/* Left - Form */}
      <div className="flex-1 flex flex-col justify-center px-8 md:px-16 lg:px-24 relative">
        <div className="absolute inset-0 hero-glow opacity-50" />
        
        <div className="relative z-10 max-w-md w-full mx-auto">
          <Link to="/" className="inline-flex items-center gap-2 text-zinc-500 hover:text-white transition-colors mb-8">
            <ArrowLeft className="w-4 h-4" /> Back
          </Link>

          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-sm bg-[#06B6D4]/20 border border-[#06B6D4]/50 flex items-center justify-center">
              <Gamepad2 className="w-6 h-6 text-[#06B6D4]" />
            </div>
            <div>
              <h1 className="text-2xl font-black tracking-tight text-white">PlayMold</h1>
              <p className="text-[10px] text-zinc-500 font-mono tracking-widest uppercase">AI Game Creator</p>
            </div>
          </div>

          <h2 className="text-3xl font-bold text-white mb-2">Welcome Back</h2>
          <p className="text-zinc-400 mb-8">Sign in to continue creating</p>

          <form onSubmit={handleSubmit} className="space-y-4">
            {needsVerify && (
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-xs text-amber-300" data-testid="login-verify-banner">
                Your email isn't verified yet. Check your inbox for the link, or{' '}
                <button type="button" onClick={resendVerification} className="underline font-bold hover:text-amber-200" data-testid="login-resend-verification">resend the email</button>.
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-zinc-400 text-xs font-mono uppercase tracking-wider">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <Input id="email" type="email" placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 bg-transparent border-[#1E1E24] focus:border-[#06B6D4] text-white" data-testid="email-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-zinc-400 text-xs font-mono uppercase tracking-wider">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <Input id="password" type={showPassword ? 'text' : 'password'} placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 pr-10 bg-transparent border-[#1E1E24] focus:border-[#06B6D4] text-white" data-testid="password-input" />
                <button type="button" onClick={() => setShowPassword(v => !v)} aria-label={showPassword ? 'Hide password' : 'Show password'}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300" data-testid="password-toggle">
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <Button type="submit" className="w-full bg-[#06B6D4] text-black hover:bg-[#06B6D4]/90" disabled={loading} data-testid="login-submit-btn">
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-zinc-500">
            Don't have an account? <Link to={`/register${nextPath !== '/dashboard' ? `?next=${encodeURIComponent(nextPath)}` : ''}`} className="text-[#06B6D4] hover:underline">Create one</Link>
          </p>
        </div>
      </div>

      {/* Right - Visual */}
      <div className="hidden lg:flex flex-1 bg-[#0A0A0B] items-center justify-center p-16 relative overflow-hidden border-l border-[#1E1E24]">
        <div className="absolute inset-0 canvas-grid opacity-50" />
        <div className="relative z-10 text-center">
          <div className="w-32 h-32 rounded-sm bg-[#06B6D4]/20 border border-[#06B6D4]/50 flex items-center justify-center mx-auto mb-6 animate-float">
            <Gamepad2 className="w-16 h-16 text-[#06B6D4]" />
          </div>
          <p className="text-xl font-bold text-white mb-2">Build Games with AI</p>
          <p className="text-zinc-500 text-sm">No coding required</p>
        </div>
      </div>
    </div>
  );
}
